/**
 * Service Workflow Service
 *
 * Handles business logic and workflows for services.
 * This service manages validations, dependency checking, and other business operations.
 */

import { RepositoryResult } from './BaseRepository';
import { getDbClient } from '@/server/lib/database';
import { Service } from '@/shared/types';

/**
 * Service dependency information
 */
export type ServiceDependencies = {
  hasDependencies: boolean;
  jobsCount: number;
  quotesCount: number;
};

/**
 * Service usage statistics
 */
export type ServiceUsageStats = {
  serviceId: string;
  serviceName: string;
  totalUsage: number;
  jobsUsage: number;
  quotesUsage: number;
  revenueGenerated: number;
};

/**
 * Service Workflow Service
 * Provides business logic and workflow operations for services
 */
export class ServiceWorkflowService {
  private tableName = 'services' as const;

  /**
   * Validate service data before creation/update
   */
  validateServiceData(serviceData: Partial<Service>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!serviceData.name || serviceData.name.trim() === '') {
      errors.push('Service name is required');
    } else if (serviceData.name.trim().length > 200) {
      errors.push('Service name must be less than 200 characters');
    }

    if (serviceData.rate === undefined || serviceData.rate === null) {
      errors.push('Service rate is required');
    } else if (serviceData.rate < 0) {
      errors.push('Service rate cannot be negative');
    } else if (serviceData.rate > 999999) {
      errors.push('Service rate is too high');
    }

    if (serviceData.description && serviceData.description.trim().length > 1000) {
      errors.push('Service description must be less than 1000 characters');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Check if a service has dependencies (jobs, quotes)
   */
  async hasDependencies(id: string): Promise<RepositoryResult<ServiceDependencies>> {
    try {
      const db = getDbClient();

      // Check for job_services
      const { count: jobsCount } = await db
        .from('job_services')
        .select('id', { count: 'exact', head: true })
        .eq('service_id', id);

      // Check for quote_items
      const { count: quotesCount } = await db
        .from('quote_items')
        .select('id', { count: 'exact', head: true })
        .eq('service_id', id);

      const hasDependencies = (jobsCount || 0) > 0 || (quotesCount || 0) > 0;

      return {
        success: true,
        data: {
          hasDependencies,
          jobsCount: jobsCount || 0,
          quotesCount: quotesCount || 0,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'checking service dependencies'),
      };
    }
  }

  /**
   * Get service usage statistics
   */
  async getUsageStats(userId: string, serviceId: string): Promise<RepositoryResult<ServiceUsageStats>> {
    try {
      const db = getDbClient();

      // Get service details
      const { data: service, error: serviceError } = await db
        .from(this.tableName)
        .select('id, name, rate')
        .eq('id', serviceId)
        .eq('user_id', userId)
        .single();

      if (serviceError || !service) {
        throw serviceError || new Error('Service not found');
      }

      // Count usage in jobs
      const { count: jobsCount } = await db
        .from('job_services')
        .select('id', { count: 'exact', head: true })
        .eq('service_id', serviceId);

      // Count usage in quotes
      const { count: quotesCount } = await db
        .from('quote_items')
        .select('id', { count: 'exact', head: true })
        .eq('service_id', serviceId);

      const totalUsage = (jobsCount || 0) + (quotesCount || 0);

      // Calculate revenue generated (rate * usage count)
      const revenueGenerated = service.rate * totalUsage;

      return {
        success: true,
        data: {
          serviceId: service.id,
          serviceName: service.name,
          totalUsage,
          jobsUsage: jobsCount || 0,
          quotesUsage: quotesCount || 0,
          revenueGenerated,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching service usage statistics'),
      };
    }
  }

  /**
   * Get usage statistics for all services of a user
   */
  async getAllUsageStats(userId: string): Promise<RepositoryResult<ServiceUsageStats[]>> {
    try {
      // Import dynamically to avoid circular dependency
      const { serviceQueryRepository } = await import('./ServiceQueryRepository');

      // Get all services for user
      const servicesResult = await serviceQueryRepository.findByUserId(userId);

      if (!servicesResult.success || !servicesResult.data) {
        return {
          success: false,
          error: 'Unable to fetch services',
        };
      }

      const services = servicesResult.data;

      // Get usage stats for each service
      const statsPromises = services.map((service) =>
        this.getUsageStats(userId, service.id)
      );

      const statsResults = await Promise.all(statsPromises);

      const allStats = statsResults
        .filter((result) => result.success && result.data)
        .map((result) => result.data!);

      return {
        success: true,
        data: allStats,
      };
    } catch (error) {
      return {
        success: false,
        error: this.handleError(error, 'fetching all service usage statistics'),
      };
    }
  }

  /**
   * Format service rate as currency
   */
  formatRate(rate: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(rate);
  }

  /**
   * Categorize service by rate
   */
  categorizeByRate(rate: number): 'low' | 'medium' | 'high' | 'premium' {
    if (rate < 50) return 'low';
    if (rate < 200) return 'medium';
    if (rate < 500) return 'high';
    return 'premium';
  }

  /**
   * Calculate revenue potential for a service
   */
  calculateRevenuePotential(rate: number, estimatedMonthlyUsage: number): number {
    return rate * estimatedMonthlyUsage;
  }

  /**
   * Get recommended rate based on market average
   */
  getRecommendedRate(category: string): { min: number; max: number; average: number } {
    const rateGuides: Record<string, { min: number; max: number; average: number }> = {
      'consulting': { min: 50, max: 500, average: 150 },
      'development': { min: 75, max: 300, average: 150 },
      'design': { min: 50, max: 250, average: 100 },
      'writing': { min: 25, max: 200, average: 75 },
      'marketing': { min: 50, max: 300, average: 125 },
      'other': { min: 25, max: 200, average: 75 },
    };

    return rateGuides[category.toLowerCase()] || rateGuides['other'];
  }

  /**
   * Validate if rate is competitive
   */
  isRateCompetitive(currentRate: number, category: string): { competitive: boolean; recommendation: string } {
    const guide = this.getRecommendedRate(category);

    if (currentRate < guide.min) {
      return {
        competitive: false,
        recommendation: `Rate is below market range. Consider raising to at least ${this.formatRate(guide.min)}.`,
      };
    }

    if (currentRate > guide.max) {
      return {
        competitive: true,
        recommendation: `Rate is above market range. Ensure your service justifies the premium pricing.`,
      };
    }

    return {
      competitive: true,
      recommendation: 'Rate is within competitive market range.',
    };
  }

  /**
   * Sanitize service name
   */
  sanitizeServiceName(name: string): string {
    return name.trim().replace(/\s+/g, ' ');
  }

  /**
   * Generate service slug from name
   */
  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Helper method to handle errors consistently
   */
  private handleError(error: any, context: string): string {
    console.error(`Error ${context}:`, error);
    return error?.message || `Failed to ${context}`;
  }
}

// Export singleton instance
export const serviceWorkflowService = new ServiceWorkflowService();
